/* eslint-disable @typescript-eslint/no-explicit-any */

import { getTenantDomain } from "@picovico/helpers";
import { getLocalStorageData } from "@picovico/utils";
import { useCallback, useEffect, useRef, useState } from "react";

interface useWebSocketProps {
  onSocketOpen?: () => void;
  onSocketClose?: () => void;
  onMessage?: (e: MessageEvent<any>) => void;
  url?: string;
}

function useWebSocket({
  onSocketOpen,
  onSocketClose,
  onMessage,
  url,
}: useWebSocketProps) {
  const [socket, setSocket] = useState<null | WebSocket>(null);
  const isSocketReady = socket?.readyState === WebSocket.OPEN;
  const [socketRequestInitiated, setSocketRequestInitiated] = useState(false);
  const [isSocketOpen, setIsSocketOpen] = useState(false);
  const [isSocketClosed, setIsSocketClosed] = useState(false);
  const [socketUrl, setSocketUrl] = useState<null | string>(url || null);

  const retryCountRef = useRef(0);
  const maxRetries = 20;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isReconnectingRef = useRef(false);
  const secure = process.env.NODE_ENV === "production" ? "wss://" : "ws://";
  const tenantDomain = getTenantDomain();
  const tenantSocketBaseUrl = `${secure}${tenantDomain}/ws`;

  useEffect(() => {
    let currentSocket: WebSocket | null = null;
    const socketBaseUrl = tenantDomain
      ? tenantSocketBaseUrl
      : process.env.BASE_WEBSOCKET_URL;
    let isMounted = true;

    const connectSocket = () => {
      if (!socketUrl || !isMounted) return;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (currentSocket) {
        currentSocket.close();
      }

      if (!isReconnectingRef.current) {
        retryCountRef.current = 0;
      }

      // console.log(
      //   `Connecting to WebSocket: ${socketUrl}, Attempt: ${retryCountRef.current + 1}`,
      // );

      try {
        currentSocket = new WebSocket(
          `${socketBaseUrl}${socketUrl}?token=${getLocalStorageData("token")}`,
        );

        currentSocket.onopen = () => {
          if (!isMounted) return;

          // console.log("WebSocket connected");
          setSocketRequestInitiated(true);
          setIsSocketOpen(true);
          setIsSocketClosed(false);

          retryCountRef.current = 0;
          isReconnectingRef.current = false;

          if (onSocketOpen) {
            onSocketOpen();
          }
        };

        currentSocket.onclose = () => {
          if (!isMounted) return;

          setSocketRequestInitiated(false);
          setIsSocketClosed(true);
          setIsSocketOpen(false);

          if (onSocketClose) {
            onSocketClose();
          }

          if (retryCountRef.current < maxRetries) {
            retryCountRef.current += 1;
            isReconnectingRef.current = true;

            const delay = Math.min(
              1000 * Math.pow(2, retryCountRef.current - 1),
              30000,
            );
            // console.log(
            //   `Retrying connection in ${delay}ms (attempt ${retryCountRef.current})`,
            // );

            reconnectTimeoutRef.current = setTimeout(() => {
              if (isMounted) {
                connectSocket();
              }
            }, delay);
          } else {
            isReconnectingRef.current = false;
            if (retryCountRef.current >= maxRetries) {
              console.log("Max retries reached, giving up");
            }
          }
        };

        currentSocket.onerror = (error) => {
          console.error("WebSocket error:", error);
        };

        currentSocket.onmessage = (e) => {
          if (onMessage && isMounted) {
            try {
              onMessage({
                ...e,
                data: JSON.parse(e.data),
              });
            } catch (parseError) {
              console.error("Error parsing WebSocket message:", parseError);
            }
          }
        };

        if (isMounted) {
          setSocket(currentSocket);
        }
      } catch (error) {
        console.error("Error creating WebSocket:", error);
        if (isMounted) {
          setIsSocketClosed(true);
          setIsSocketOpen(false);
        }
      }
    };

    connectSocket();

    return () => {
      isMounted = false;
      isReconnectingRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (currentSocket) {
        currentSocket.close(1000, "Component unmounting");
        currentSocket = null;
      }

      setSocket(null);
      setIsSocketOpen(false);
      setSocketRequestInitiated(false);
    };
  }, [socketUrl, onSocketClose, onSocketOpen, tenantDomain, tenantSocketBaseUrl, onMessage]);

  const sendSocketMessage = useCallback(
    (message: any) => {
      console.log("Websocket.open", WebSocket.OPEN);
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify(message));
        } catch (error) {
          console.error("Error sending WebSocket message:", error);
        }
      } else {
        console.warn("WebSocket is not open, message not sent:", message);
      }
    },
    [socket],
  );

  const setupSocket = useCallback((URL: string) => {
    // console.log("setupSocket", URL);
    isReconnectingRef.current = false;
    retryCountRef.current = 0;
    setSocketUrl(URL);
  }, []);

  const closeSocket = useCallback(() => {
    if (socket) {
      socket.close(1000, "Manual close");
    }
  }, [socket]);

  return {
    socket,
    sendSocketMessage,
    closeSocket,
    isSocketReady,
    setupSocket,
    isSocketOpen,
    isSocketConnecting: socket
      ? socket?.readyState === WebSocket.CONNECTING
      : false,
    socketRequestInitiated,
    isSocketClosed,
    retryCount: retryCountRef.current,
  };
}

export default useWebSocket;
